<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { uploadImage, uploadFile } from "@/lib/uploads";
import { retrieveMapDataList, retrieveMapDetails, addOrEditMapDetails } from "./api";

const route = useRoute(), router = useRouter();
const scriptName = computed(() => route.params.id || null);
const isEdit = computed(() => !!scriptName.value);
const loading = ref(false), saving = ref(false), error = ref(""), successMsg = ref(""), uploadingIndex = ref(null);
const pageName = ref(""), banners = ref([]), eventPages = ref([]), stampCards = ref([]);
const previewDialog = ref(false), previewDetailIndex = ref(null);
const imgInputRefs = ref([]);
const pdfInputRefs = ref([]);

const bannerTypeItems = [
  { title: "External Link", value: "link" },
  { title: "Upload PDF", value: "upload" },
  { title: "Another Event Page", value: "event" },
  { title: "Stamp Card", value: "stampcard" },
  { title: "Livestream", value: "livestream" },
  { title: "Custom Page", value: "custom" },
];
const eventPageItems = computed(() => eventPages.value.map(p => ({ title: p.pageName, value: p.scriptName })));
const stampCardItems = computed(() => stampCards.value.map(s => ({ title: s.name, value: s.id })));

const newBanner = (sn) => ({ sn, alt: "", bannerType: "upload", clickAction: "internal", src: "", tempUrl: "", url: "", param: "", paramValue: "" });

function fromConfig(item, index) {
  const copy = { ...item };
  copy.sn = copy.sn || index + 1;
  copy.url = copy.url || ""; copy.param = copy.param || ""; copy.paramValue = copy.paramValue || ""; copy.src = copy.src || ""; copy.tempUrl = "";
  if (copy.clickAction === "redirect") { copy.bannerType = "link"; }
  else {
    switch (copy.url) {
      case "pdf": copy.bannerType = "upload"; break;
      case "Dynamic Home Page": copy.bannerType = "event"; copy.tempUrl = copy.paramValue; break;
      case "Dynamic Stamp Card": copy.bannerType = "stampcard"; copy.tempUrl = copy.paramValue; break;
      case "Live Stream Teaser": copy.bannerType = "livestream"; break;
      default: copy.bannerType = "custom"; break;
    }
  }
  return copy;
}

onMounted(async () => {
  loading.value = true;
  try {
    const { scriptList, stampCardList } = await retrieveMapDataList();
    eventPages.value = Array.isArray(scriptList) ? scriptList : [];
    stampCards.value = Array.isArray(stampCardList) ? stampCardList : [];
    if (isEdit.value) {
      const current = eventPages.value.find(p => p.scriptName === scriptName.value);
      pageName.value = current?.pageName || scriptName.value || "";
      const config = await retrieveMapDetails(scriptName.value);
      banners.value = (Array.isArray(config) ? config : []).map(fromConfig);
      if (!banners.value.length) banners.value.push(newBanner(1));
    } else {
      banners.value.push(newBanner(1));
    }
  } catch { error.value = "Failed to load event page data."; }
  finally { loading.value = false; }
});

async function onImage(e, i) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  uploadingIndex.value = i;
  try { banners.value[i].src = await uploadImage("campaign", file); }
  catch { error.value = "Couldn't upload image."; }
  finally { uploadingIndex.value = null; }
}

async function onPdf(e, i) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  uploadingIndex.value = i;
  try { banners.value[i].paramValue = await uploadFile("campaign", file); }
  catch { error.value = "Couldn't upload PDF."; }
  finally { uploadingIndex.value = null; }
}

function buildConfig() {
  return banners.value.slice().sort((a, b) => a.sn - b.sn).map(it => {
    const item = { ...it };
    switch (item.bannerType) {
      case "link": item.clickAction = "redirect"; item.param = ""; item.paramValue = ""; break;
      case "upload": item.clickAction = "internal"; item.url = "pdf"; item.param = item.paramValue?.includes(".pdf") ? "pdfLink" : "blank"; if (!item.param.includes("pdfLink")) item.paramValue = "blank"; break;
      case "event": item.clickAction = "internal"; item.url = "Dynamic Home Page"; item.param = "bannerid"; item.paramValue = item.tempUrl || item.paramValue; break;
      case "stampcard": item.clickAction = "internal"; item.url = "Dynamic Stamp Card"; item.param = "stampid"; item.paramValue = item.tempUrl || item.paramValue; break;
      case "livestream": item.clickAction = "internal"; item.url = "Live Stream Teaser"; item.param = item.param || "blank"; item.paramValue = item.paramValue || "blank"; break;
      default: item.clickAction = "internal"; item.param = item.param || "blank"; item.paramValue = item.paramValue || "blank"; break;
    }
    return { src: item.src || null, alt: item.alt || "", clickAction: item.clickAction, url: item.url || "", param: item.param || "blank", paramValue: item.paramValue || "blank", sn: item.sn };
  });
}

async function submit() {
  error.value = ""; successMsg.value = "";
  if (!pageName.value.trim()) { error.value = "Page name is required."; return; }
  saving.value = true;
  try {
    const res = await addOrEditMapDetails({ config: buildConfig(), pageName: pageName.value, scriptName: isEdit.value ? scriptName.value : null });
    if (res?.status) successMsg.value = `Saved successfully. Link: https://aspials.com/campaign/bannerpages/${res.data}`;
    else error.value = "Failed to save.";
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}

const sortedBanners = computed(() => banners.value.slice().sort((a, b) => a.sn - b.sn));
const previewItem = computed(() => previewDetailIndex.value !== null ? banners.value[previewDetailIndex.value] : null);
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'event-homepages' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit" : "Add" }} Event Home Page</h1>
    <p v-if="loading">Loading…</p>
    <template v-else>
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="successMsg" type="success" class="mb-4">{{ successMsg }}</VAlert>
      <VTextField v-model="pageName" label="Event Name" class="mb-6" style="max-width:480px" />

      <VTable density="compact" class="mb-2">
        <thead>
          <tr>
            <th style="width:70px">Order</th>
            <th style="width:130px">Banner Name</th>
            <th style="width:240px">Banner Image</th>
            <th style="width:190px">Type</th>
            <th>Link / Upload</th>
            <th style="width:48px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(b, i) in banners" :key="i">
            <td>
              <VTextField v-model.number="b.sn" type="number" density="compact" hide-details style="width:64px" />
            </td>
            <td>
              <VTextField v-model="b.alt" density="compact" hide-details placeholder="Banner Name" />
            </td>
            <td>
              <input :ref="el => { if(el) imgInputRefs[i] = el }" type="file" accept="image/*" style="display:none" @change="(e) => onImage(e, i)" />
              <VBtn color="primary" size="small" prepend-icon="ri-image-line" @click="imgInputRefs[i]?.click()">Upload Banner Image</VBtn>
              <span v-if="uploadingIndex === i" class="text-caption ml-2">Uploading…</span>
              <div v-if="b.src" class="mt-1">
                <img :src="b.src" style="max-height:56px; max-width:120px; border-radius:4px" />
              </div>
            </td>
            <td>
              <VSelect v-model="b.bannerType" :items="bannerTypeItems" item-title="title" item-value="value" density="compact" hide-details label="Type" />
            </td>
            <td style="max-width:200px; word-break:break-word; white-space:normal">
              <template v-if="b.bannerType === 'link'">
                <VTextField v-model="b.url" density="compact" hide-details placeholder="External URL" />
              </template>
              <template v-else-if="b.bannerType === 'upload'">
                <input :ref="el => { if(el) pdfInputRefs[i] = el }" type="file" accept="application/pdf" style="display:none" @change="(e) => onPdf(e, i)" />
                <VBtn size="small" color="primary" prepend-icon="ri-file-line" @click="pdfInputRefs[i]?.click()">Upload</VBtn>
                <div v-if="b.paramValue && b.paramValue !== 'blank'" class="text-caption mt-1 text-truncate" style="max-width:200px">{{ b.paramValue.split('/').pop() }}</div>
              </template>
              <template v-else-if="b.bannerType === 'event'">
                <VSelect v-model="b.tempUrl" :items="eventPageItems" item-title="title" item-value="value" density="compact" hide-details label="Select Event Page" />
              </template>
              <template v-else-if="b.bannerType === 'stampcard'">
                <VSelect v-model="b.tempUrl" :items="stampCardItems" item-title="title" item-value="value" density="compact" hide-details label="Select Stamp Card" />
              </template>
              <template v-else-if="b.bannerType === 'custom'">
                <VTextField v-model="b.url" density="compact" hide-details placeholder="Custom Page Name" />
              </template>
            </td>
            <td>
              <VBtn v-if="banners.length > 1" icon size="small" color="error" variant="text" @click="banners.splice(i, 1); banners.forEach((bx, j) => bx.sn = j + 1)">
                <VIcon>ri-delete-bin-line</VIcon>
              </VBtn>
            </td>
          </tr>
        </tbody>
      </VTable>

      <div class="d-flex justify-space-between align-center mt-4">
        <VBtn color="primary" @click="banners.push(newBanner(banners.length + 1))">Add More Banners</VBtn>
        <VBtn color="primary" variant="tonal" @click="previewDialog = true; previewDetailIndex = null">Preview</VBtn>
        <VBtn color="success" :loading="saving" :disabled="uploadingIndex !== null" @click="submit">Save</VBtn>
      </div>
    </template>

    <!-- PREVIEW DIALOG -->
    <VDialog v-model="previewDialog" max-width="440">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between pt-4">
          Preview
          <VBtn v-if="previewDetailIndex !== null" variant="text" size="small" @click="previewDetailIndex = null">
            <VIcon start>ri-arrow-left-line</VIcon> Back
          </VBtn>
        </VCardTitle>
        <VCardText class="pa-2">
          <div v-if="previewDetailIndex === null" style="width:400px; height:600px; border:1px solid #ccc; overflow-y:auto; padding:8px">
            <div v-for="(b, i) in sortedBanners" :key="i" class="mb-3" style="cursor:pointer" @click="previewDetailIndex = banners.indexOf(b)">
              <img v-if="b.src" :src="b.src" style="width:100%; border:1px solid #ddd; border-radius:4px" />
              <div v-else style="width:100%; height:80px; background:#eee; border-radius:4px; display:flex; align-items:center; justify-content:center" class="text-caption text-disabled">No image</div>
              <div class="text-caption mt-1">{{ b.alt || '(no name)' }}</div>
            </div>
          </div>
          <div v-else style="width:400px; height:600px; border:1px solid #ccc; overflow:hidden; display:flex; align-items:center; justify-content:center">
            <div v-if="previewItem?.bannerType === 'upload'" class="text-center text-disabled">No preview available for PDF</div>
            <iframe v-else-if="previewItem?.url" :src="previewItem.url" style="width:400px; height:600px; border:0" />
            <div v-else class="text-center text-disabled text-caption">No preview available</div>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="previewDialog = false">Close</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
